import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router';

import { TableStoreContext } from '../../../stores/TableStoreContext';
import { getQueryParams, noResultsFound } from '../../../lib/utils';
import { TableBlockCombinedProps } from './types';
import { getNormalizedFilters } from './utils';

const usePresenter = (props: TableBlockCombinedProps): TableBlockCombinedProps => {
  const { databaseSlug, tableSlug, query, handleClearAllFilters } = props;
  const { t } = useTranslation();
  const { search } = useLocation();
  const queryParams = getQueryParams(search);
  const page = queryParams.page ? Number(queryParams.page) : 1;
  const perPage = queryParams.perPage ? Number(queryParams.perPage) : 100;

  const tableStore = useContext(TableStoreContext);
  const { filters, sortBy, sortOrder } = tableStore.tableConfigurationState?.[tableSlug || ''] || {};
  const normalizedFilters = getNormalizedFilters(filters);

  const { isLoading: isTableLoading } = useQuery(['getTable', databaseSlug, tableSlug], () => {
    if (databaseSlug && tableSlug) {
      return tableStore.fetchTable(databaseSlug, tableSlug);
    }
  }, { cacheTime: 0 });
  const { isLoading: areEntriesLoading } = useQuery([
    'getTableEntries',
    databaseSlug,
    tableSlug,
    page,
    perPage,
    query,
    sortBy,
    sortOrder,
    filters,
  ], () => {
    if (databaseSlug && tableSlug) {
      return tableStore.fetchEntries(
        databaseSlug,
        tableSlug,
        { page, perPage },
        query,
        sortBy && sortOrder ? [sortBy, sortOrder] : undefined,
        normalizedFilters,
      );
    }
  }, {
    enabled: !!tableStore.table && !!tableStore.tableConfigurationState,
    cacheTime: 0,
  });

  const filtersApplied = !!(query || (filters && Object.keys(filters).length));

  return {
    ...props,
    table: {
      ...props?.table,
      areEntriesLoading,
      query,
    },
    state: noResultsFound(tableStore?.tableState?.length || 0, page, filtersApplied) ? 'NoResults' : 'Default',
    userPageBar: {
      page,
      perPage,
      totalItems: tableStore.tableTotals.totalItems,
      totalPages: tableStore.tableTotals.totalPages,
    },
    isLoading: isTableLoading,
    showModal: tableStore.isModalOpen,
    title: {
      value: t('database_page.no_results_found.title'),
    },
    instructions: {
      value: t('database_page.no_results_found.instructions'),
    },
    clearFiltersButton: {
      text: { 
        value: t('database_page.no_results_found.clear_filters'),
      },
      onClick: handleClearAllFilters,
    },
  };
};

export default usePresenter;
